import React, { useState, useMemo } from 'react';
import {
  ArrowLeftRight, Plus, ChevronDown, ChevronRight,
  Play, Square, Trash2, RefreshCw,
} from 'lucide-react';
import { getTheme } from '../App';
import { useWS } from '../WSContext';

interface Props { dark?: boolean; }
interface CloseForm { chunkUsd: string; tpRatio: string; }

const fmtSym   = (s: string) => s.replace('PERP_','').replace('_USDC','').replace('-PERP','');
const fmtPrice = (v: number) => !v ? '—' : v < 1 ? v.toFixed(5) : v.toFixed(4);

export default function PairTrades({ dark = true }: Props) {
  const { tickers: rawTickers, sendWs, connected, subscribe } = useWS() as any;
  const t = getTheme(dark);

  // ── Ticker map ───────────────────────────────────────────────────────────────
  const tickers = useMemo(() => {
    const m: Record<string, any> = {};
    (rawTickers as any[]).forEach((tk: any) => { m[tk.symbol] = tk; });
    return m;
  }, [rawTickers]);

  const symbols = useMemo(
    () => (rawTickers as any[]).map((tk: any) => tk.symbol).sort(),
    [rawTickers],
  );

  // ── Pair trades state (WS'den gelir) ────────────────────────────────────────
  const [pairTrades, setPairTrades] = useState<any[]>([]);

  React.useEffect(() => {
    if (!connected) return;
    sendWs({ type: 'get_pair_trades' });
  }, [connected]);

  React.useEffect(() => {
    return subscribe((data: any) => {
      if (data.type === 'pair_trades') {
        setPairTrades(data.payload || []);
      }
    });
  }, [subscribe]);

  // ── Form state (yeni pair) ───────────────────────────────────────────────────
  const [formOpen,  setFormOpen]  = useState(true);
  const [name,      setName]      = useState('');
  const [symA,      setSymA]      = useState('');
  const [dirA,      setDirA]      = useState<'long'|'short'>('long');
  const [symB,      setSymB]      = useState('');
  const [dirB,      setDirB]      = useState<'long'|'short'>('short');
  const [notional,  setNotional]  = useState('100');
  const [leverage,  setLeverage]  = useState('1');
  const [epA,       setEpA]       = useState('');
  const [epB,       setEpB]       = useState('');

  // Sembol seçilmemişse ilk iki ticker'ı varsayılan yap
  const effSymA = symA || symbols[0] || '';
  const effSymB = symB || symbols[1] || '';

  const tkA    = tickers[effSymA];
  const tkB    = tickers[effSymB];
  const markA  = tkA?.mark_price  || 0;
  const markB  = tkB?.mark_price  || 0;
  const entryA = parseFloat(epA) || (dirA === 'long' ? tkA?.best_bid : tkA?.best_ask) || markA;
  const entryB = parseFloat(epB) || (dirB === 'long' ? tkB?.best_bid : tkB?.best_ask) || markB;
  const ntl    = parseFloat(notional) || 0;
  const qtyA   = entryA > 0 ? ntl / entryA : 0;
  const qtyB   = entryB > 0 ? ntl / entryB : 0;
  const ratio  = entryA > 0 && entryB > 0 ? entryA / entryB : 0;
  const canSubmit = effSymA && effSymB && effSymA !== effSymB && ntl > 0 && entryA > 0 && entryB > 0;

  const handleCreate = () => {
    if (!canSubmit) return;
    sendWs({
      type: 'create_pair_trade',
      payload: {
        name:         name || `${fmtSym(effSymA)}/${fmtSym(effSymB)}`,
        notional_usd: ntl,
        leverage:     parseInt(leverage) || 1,
        leg_a: { symbol: effSymA, direction: dirA, entry_price: entryA },
        leg_b: { symbol: effSymB, direction: dirB, entry_price: entryB },
      },
    });
    // Formu sıfırla
    setName(''); setEpA(''); setEpB('');
    setFormOpen(false);
  };

  // ── Kapatma form state ───────────────────────────────────────────────────────
  const [closeForms,   setCloseForms]   = useState<Record<string, CloseForm>>({});
  const [closeOpenId,  setCloseOpenId]  = useState<string|null>(null);
  const [historyOpen,  setHistoryOpen]  = useState(false);

  const getCloseForm = (id: string): CloseForm =>
    closeForms[id] || { chunkUsd: '50', tpRatio: '' };
  const setCloseField = (id: string, f: keyof CloseForm, v: string) =>
    setCloseForms(p => ({ ...p, [id]: { ...getCloseForm(id), [f]: v } }));

  const startClose = (pair: any) => {
    const form = getCloseForm(pair.id);
    sendWs({
      type: 'start_pair_close',
      payload: {
        pair_id:        pair.id,
        chunk_size_usd: parseFloat(form.chunkUsd) || 50,
        tp_ratio:       parseFloat(form.tpRatio)  || 0,
      },
    });
    setCloseOpenId(null);
  };

  const stopClose  = (pair: any) =>
    sendWs({ type: 'stop_pair_close',   payload: { pair_id: pair.id } });
  const deletePair = (pair: any) =>
    sendWs({ type: 'delete_pair_trade', payload: { pair_id: pair.id } });

  // ── Hesaplamalar ─────────────────────────────────────────────────────────────
  const getPairPnl = (pair: any) => {
    const legPnl = (leg: any) => {
      if (!leg) return 0;
      const tk  = tickers[leg.symbol];
      if (!tk)  return 0;
      const rem = leg.total_qty - leg.closed_qty;
      return (tk.mark_price - leg.entry_price) * rem * (leg.direction === 'long' ? 1 : -1);
    };
    return legPnl(pair.leg_a) + legPnl(pair.leg_b);
  };

  const getCurrentRatio = (pair: any): number | null => {
    const tkA = tickers[pair.leg_a?.symbol];
    const tkB = tickers[pair.leg_b?.symbol];
    if (!tkA || !tkB || !tkB.mark_price) return null;
    return tkA.mark_price / tkB.mark_price;
  };

  const chunkPct = (pair: any) => {
    const a = pair.leg_a, b = pair.leg_b;
    if (!a?.total_qty || !b?.total_qty) return 0;
    return Math.min(((a.closed_qty/a.total_qty + b.closed_qty/b.total_qty) / 2) * 100, 100);
  };

  // ── Listeleri ayır ───────────────────────────────────────────────────────────
  const active = pairTrades.filter(p => p.status !== 'closed');
  const closed = pairTrades.filter(p => p.status === 'closed');

  // ── Style yardımcıları ───────────────────────────────────────────────────────
  const card   = `${t.card} border ${t.cardBorder} rounded-xl`;
  const inp    = `border rounded-lg px-2 py-1.5 text-xs focus:outline-none ${t.inp}`;
  const statusColor: Record<string,string> = {
    opening: 'text-amber-400', open: 'text-emerald-400',
    closing: 'text-blue-400',  closed: 'text-gray-400',
  };
  const statusLabel: Record<string,string> = {
    opening: 'Açılıyor', open: 'Açık', closing: 'Kapanıyor', closed: 'Kapalı',
  };

  const DirBtn = ({ val, cur, onChange }: { val: 'long'|'short'; cur: string; onChange: (v: 'long'|'short') => void }) => (
    <button onClick={() => onChange(val)}
      className={`px-3 py-1.5 rounded text-xs font-bold border transition-colors ${
        cur === val
          ? val === 'long'
            ? 'bg-emerald-600 border-emerald-500 text-white'
            : 'bg-rose-600 border-rose-500 text-white'
          : t.btnSecondary
      }`}>
      {val === 'long' ? 'LONG' : 'SHORT'}
    </button>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-5">

      {/* Başlık */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ArrowLeftRight size={18} className="text-[#4A9FE8]" />
          <h2 className={`text-xl font-bold ${t.text}`}>Pair Trade'ler</h2>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => sendWs({ type: 'get_pair_trades' })}
            className={`p-1.5 rounded-lg border ${t.cardBorder} ${t.muted} hover:text-white transition-colors`}>
            <RefreshCw size={13} />
          </button>
          <div className={`flex items-center gap-2 text-xs ${t.muted}`}>
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-rose-400'}`} />
            {connected ? 'Canlı' : 'Bağlantı bekleniyor...'}
          </div>
        </div>
      </div>

      {/* ── YENİ PAİR EKLE ────────────────────────────────────────────────────── */}
      <div className={`${card} overflow-hidden`}>
        <div className={`px-4 py-3 flex items-center justify-between cursor-pointer border-b ${t.cardBorder}`}
          onClick={() => setFormOpen(o => !o)}>
          <div className="flex items-center gap-2">
            {formOpen
              ? <ChevronDown size={14} className={t.muted} />
              : <ChevronRight size={14} className={t.muted} />}
            <Plus size={14} className="text-[#4A9FE8]" />
            <span className={`text-sm font-bold ${t.text}`}>Yeni Pair Trade Ekle</span>
          </div>
        </div>

        {formOpen && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">

              {/* Leg A */}
              <div className={`border ${t.cardBorder} rounded-lg p-3 space-y-2`}>
                <p className={`text-xs font-semibold text-emerald-400`}>LEG A</p>
                <div className="flex gap-2">
                  <select className={`${inp} flex-1`} value={effSymA}
                    onChange={e => setSymA(e.target.value)}>
                    {symbols.map((s: string) => (
                      <option key={s} value={s}>{fmtSym(s)}</option>
                    ))}
                  </select>
                  <DirBtn val="long"  cur={dirA} onChange={setDirA} />
                  <DirBtn val="short" cur={dirA} onChange={setDirA} />
                </div>
                <div>
                  <label className={`text-xs ${t.muted}`}>Giriş Fiyatı ($)</label>
                  <input type="number" step="any" className={`${inp} w-full mt-1`}
                    placeholder={fmtPrice(dirA === 'long' ? tkA?.best_bid || 0 : tkA?.best_ask || 0)}
                    value={epA} onChange={e => setEpA(e.target.value)} />
                </div>
                {tkA && (
                  <div className={`text-xs ${t.muted} space-y-0.5`}>
                    <p>Mark: <span className="font-mono">${fmtPrice(markA)}</span>
                      &nbsp;·&nbsp;Bid: <span className="font-mono text-emerald-400">${fmtPrice(tkA.best_bid)}</span>
                      &nbsp;·&nbsp;Ask: <span className="font-mono text-rose-400">${fmtPrice(tkA.best_ask)}</span>
                    </p>
                    <p>Adet: <span className={`font-mono font-bold ${t.text}`}>{qtyA > 0 ? qtyA.toFixed(4) : '—'}</span></p>
                  </div>
                )}
              </div>

              {/* Leg B */}
              <div className={`border ${t.cardBorder} rounded-lg p-3 space-y-2`}>
                <p className={`text-xs font-semibold text-rose-400`}>LEG B</p>
                <div className="flex gap-2">
                  <select className={`${inp} flex-1`} value={effSymB}
                    onChange={e => setSymB(e.target.value)}>
                    {symbols.map((s: string) => (
                      <option key={s} value={s}>{fmtSym(s)}</option>
                    ))}
                  </select>
                  <DirBtn val="long"  cur={dirB} onChange={setDirB} />
                  <DirBtn val="short" cur={dirB} onChange={setDirB} />
                </div>
                <div>
                  <label className={`text-xs ${t.muted}`}>Giriş Fiyatı ($)</label>
                  <input type="number" step="any" className={`${inp} w-full mt-1`}
                    placeholder={fmtPrice(dirB === 'long' ? tkB?.best_bid || 0 : tkB?.best_ask || 0)}
                    value={epB} onChange={e => setEpB(e.target.value)} />
                </div>
                {tkB && (
                  <div className={`text-xs ${t.muted} space-y-0.5`}>
                    <p>Mark: <span className="font-mono">${fmtPrice(markB)}</span>
                      &nbsp;·&nbsp;Bid: <span className="font-mono text-emerald-400">${fmtPrice(tkB.best_bid)}</span>
                      &nbsp;·&nbsp;Ask: <span className="font-mono text-rose-400">${fmtPrice(tkB.best_ask)}</span>
                    </p>
                    <p>Adet: <span className={`font-mono font-bold ${t.text}`}>{qtyB > 0 ? qtyB.toFixed(4) : '—'}</span></p>
                  </div>
                )}
              </div>
            </div>

            {/* Alt parametreler */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={`text-xs block mb-1 ${t.muted}`}>Strateji Adı</label>
                <input className={`${inp} w-full`}
                  placeholder={effSymA && effSymB ? `${fmtSym(effSymA)}/${fmtSym(effSymB)}` : 'Opsiyonel'}
                  value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div>
                <label className={`text-xs block mb-1 ${t.muted}`}>Notional (USDC / leg)</label>
                <input type="number" min="1" step="10" className={`${inp} w-full`}
                  value={notional} onChange={e => setNotional(e.target.value)} />
              </div>
              <div>
                <label className={`text-xs block mb-1 ${t.muted}`}>Kaldıraç (x)</label>
                <input type="number" min="1" max="20" step="1" className={`${inp} w-full`}
                  value={leverage} onChange={e => setLeverage(e.target.value)} />
              </div>
            </div>

            {/* Özet + gönder */}
            <div className="flex items-center justify-between">
              <div className={`text-xs ${t.muted}`}>
                {ratio > 0 && (
                  <>
                    <span>Giriş Ratio: </span>
                    <span className={`font-mono font-bold ${t.text}`}>{ratio.toFixed(6)}</span>
                    <span className="mx-2">·</span>
                    <span>Toplam: </span>
                    <span className={`font-mono font-bold ${t.text}`}>${(ntl * 2).toFixed(0)} USDC</span>
                  </>
                )}
              </div>
              <button onClick={handleCreate} disabled={!canSubmit}
                className="px-6 py-2 rounded-lg text-xs font-bold bg-[#4A9FE8] hover:bg-[#e5a820] text-[#061530] disabled:opacity-40 transition-colors">
                Emirleri Gönder
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── AKTİF PAİR TRADE'LER ──────────────────────────────────────────────── */}
      {active.length === 0 ? (
        <div className={`${card} p-8 text-center ${t.muted} text-sm`}>
          Aktif pair trade yok. Yukarıdan yeni bir pair oluşturun.
        </div>
      ) : active.map(pair => {
        const pnl          = getPairPnl(pair);
        const curRatio     = getCurrentRatio(pair);
        const entryRatio   = pair.entry_ratio || 0;
        const ratioDiff    = curRatio && entryRatio
          ? ((curRatio - entryRatio) / entryRatio) * 100 : null;
        const job          = pair.close_job   || {};
        const cfg          = pair.close_config || {};
        const pct          = chunkPct(pair);
        const tpRatio      = parseFloat(cfg.tp_ratio) || 0;
        const ratioOk      = curRatio !== null && tpRatio > 0 && curRatio >= tpRatio;
        const isCloseOpen  = closeOpenId === pair.id;
        const closeForm    = getCloseForm(pair.id);

        return (
          <div key={pair.id} className={`${card} overflow-hidden`}>

            {/* Pair başlık */}
            <div className={`px-4 py-3 flex items-center justify-between border-b ${t.cardBorder}`}>
              <div className="flex items-center gap-3">
                <span className={`text-sm font-bold ${t.text}`}>{pair.name}</span>
                <span className={`text-xs font-medium ${statusColor[pair.status] || 'text-gray-400'}`}>
                  {statusLabel[pair.status] || pair.status}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-bold ${pnl >= 0 ? t.positive : t.negative}`}>
                  {pnl >= 0 ? '+' : ''}${pnl.toFixed(4)}
                </span>
                {pair.status === 'open' && (
                  <button onClick={() => setCloseOpenId(isCloseOpen ? null : pair.id)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs border border-blue-500/60 text-blue-400 hover:bg-blue-500/20 transition-colors">
                    <Play size={10} /> Kapat
                  </button>
                )}
                {pair.status === 'closing' && (
                  <button onClick={() => stopClose(pair)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs border border-amber-500/60 text-amber-400 hover:bg-amber-500/20 transition-colors">
                    <Square size={10} /> Durdur
                  </button>
                )}
                <button onClick={() => deletePair(pair)}
                  className={`p-1.5 rounded ${t.muted} hover:text-rose-400 transition-colors`}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>

            {/* Ratio bilgisi */}
            <div className={`px-4 py-2 flex items-center gap-5 text-xs border-b ${t.cardBorder} ${t.muted}`}>
              <span>
                Giriş Ratio:&nbsp;
                <span className={`font-mono font-bold ${t.text}`}>
                  {entryRatio > 0 ? entryRatio.toFixed(6) : '—'}
                </span>
              </span>
              {curRatio !== null && (
                <>
                  <span>
                    Güncel:&nbsp;
                    <span className={`font-mono font-bold ${t.text}`}>{curRatio.toFixed(6)}</span>
                  </span>
                  {ratioDiff !== null && (
                    <span className={ratioDiff >= 0 ? t.positive : t.negative}>
                      {ratioDiff >= 0 ? '▲' : '▼'}{Math.abs(ratioDiff).toFixed(3)}%
                    </span>
                  )}
                  {tpRatio > 0 && (
                    <span>
                      TP Ratio:&nbsp;
                      <span className={`font-mono ${ratioOk ? 'text-emerald-400 font-bold' : t.text}`}>
                        {tpRatio.toFixed(6)}
                      </span>
                      {ratioOk && <span className="ml-1 text-emerald-400">✓ Aktif</span>}
                    </span>
                  )}
                </>
              )}
              <span className={t.muted}>
                Kaldıraç: {pair.leverage}x
              </span>
            </div>

            {/* İki leg */}
            <div className="grid grid-cols-2 gap-3 p-4">
              {[pair.leg_a, pair.leg_b].map((leg: any, i: number) => {
                if (!leg) return null;
                const tk     = tickers[leg.symbol];
                const mark   = tk?.mark_price || 0;
                const rem    = leg.total_qty - leg.closed_qty;
                const legPnl = mark
                  ? (mark - leg.entry_price) * rem * (leg.direction === 'long' ? 1 : -1)
                  : 0;
                const closedPct = leg.total_qty > 0
                  ? (leg.closed_qty / leg.total_qty) * 100 : 0;

                return (
                  <div key={i} className={`border ${t.cardBorder} rounded-lg p-3 space-y-2`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                          leg.direction === 'long'
                            ? 'bg-emerald-900/50 text-emerald-400'
                            : 'bg-rose-900/50 text-rose-400'
                        }`}>{leg.direction.toUpperCase()}</span>
                        <span className={`text-sm font-bold ${t.text}`}>{fmtSym(leg.symbol)}</span>
                      </div>
                      <span className={`text-xs font-bold ${legPnl >= 0 ? t.positive : t.negative}`}>
                        {legPnl >= 0 ? '+' : ''}${legPnl.toFixed(4)}
                      </span>
                    </div>
                    <div className={`text-xs ${t.muted} space-y-0.5`}>
                      <p>
                        Kalan:&nbsp;
                        <span className={`font-mono font-bold ${t.text}`}>{rem.toFixed(4)}</span>
                        &nbsp;/&nbsp;{leg.total_qty.toFixed(4)}
                      </p>
                      <p>
                        Giriş: <span className="font-mono">${fmtPrice(leg.entry_price)}</span>
                        {mark > 0 && <>&nbsp;·&nbsp;Mark: <span className="font-mono">${fmtPrice(mark)}</span></>}
                      </p>
                      {leg.closed_qty > 0 && (
                        <p className="text-blue-400">
                          {closedPct.toFixed(1)}% kapatıldı ({leg.closed_qty.toFixed(4)})
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Kapanış progress */}
            {pair.status === 'closing' && (
              <div className={`px-4 pb-3 space-y-1.5`}>
                <div className="flex items-center justify-between text-xs">
                  <span className={t.muted}>
                    Chunk #{(job.chunk_index || 0) + 1}&nbsp;·&nbsp;
                    {job.state === 'monitoring'
                      ? (job.leg_a_filled && job.leg_b_filled
                        ? 'Tamamlandı'
                        : job.leg_a_filled ? 'Leg A ✓  Leg B ⏳'
                        : job.leg_b_filled ? 'Leg A ⏳  Leg B ✓'
                        : 'İkisi de bekleniyor')
                      : job.state === 'paused'
                      ? `Ratio bekleniyor (${curRatio?.toFixed(4)} / ${tpRatio.toFixed(4)})`
                      : 'İşleniyor...'}
                  </span>
                  <span className={`font-mono text-xs ${t.text}`}>{pct.toFixed(1)}%</span>
                </div>
                <div className={`w-full h-1.5 rounded-full overflow-hidden bg-[#0a2560]`}>
                  <div className="h-full rounded-full bg-blue-500 transition-all duration-500"
                    style={{ width: `${pct}%` }} />
                </div>
              </div>
            )}

            {/* Kapatma formu (inline, açılır-kapanır) */}
            {pair.status === 'open' && isCloseOpen && (
              <div className={`border-t ${t.cardBorder} p-4 space-y-3`}>
                <p className={`text-xs font-semibold ${t.text}`}>Kapatma Ayarları</p>
                {curRatio !== null && (
                  <p className={`text-xs ${t.muted}`}>
                    Güncel Ratio:&nbsp;
                    <span className={`font-mono font-bold ${t.text}`}>{curRatio.toFixed(6)}</span>
                  </p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`text-xs block mb-1 ${t.muted}`}>Chunk Büyüklüğü (USDC)</label>
                    <input type="number" min="1" step="10" className={`${inp} w-full`}
                      value={closeForm.chunkUsd}
                      onChange={e => setCloseField(pair.id, 'chunkUsd', e.target.value)} />
                  </div>
                  <div>
                    <label className={`text-xs block mb-1 ${t.muted}`}>
                      TP Ratio Eşiği
                      <span className={`ml-1 opacity-60`}>(bu değerde kapanır)</span>
                    </label>
                    <input type="number" step="any" className={`${inp} w-full`}
                      placeholder={curRatio ? curRatio.toFixed(6) : '0'}
                      value={closeForm.tpRatio}
                      onChange={e => setCloseField(pair.id, 'tpRatio', e.target.value)} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => startClose(pair)}
                    className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs py-2 rounded-lg transition-colors">
                    Kapatmayı Başlat
                  </button>
                  <button onClick={() => setCloseOpenId(null)}
                    className={`px-4 text-xs py-2 rounded-lg ${t.btnSecondary}`}>
                    İptal
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* ── GEÇMİŞ (kapalı) PAİR TRADE'LER ─────────────────────────────────── */}
      {closed.length > 0 && (
        <div className={`${card} overflow-hidden`}>
          <div className={`px-4 py-3 flex items-center justify-between cursor-pointer`}
            onClick={() => setHistoryOpen(o => !o)}>
            <div className="flex items-center gap-2">
              {historyOpen
                ? <ChevronDown size={14} className={t.muted} />
                : <ChevronRight size={14} className={t.muted} />}
              <span className={`text-sm font-bold ${t.muted}`}>Kapatılmış Pair Trade'ler</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${t.card} border ${t.cardBorder} ${t.muted}`}>
                {closed.length}
              </span>
            </div>
          </div>
          {historyOpen && (
            <div className="divide-y divide-[#ffffff10]">
              {closed.map(pair => {
                const la = pair.leg_a, lb = pair.leg_b;
                return (
                  <div key={pair.id} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <span className={`text-sm font-semibold ${t.text}`}>{pair.name}</span>
                      <div className={`text-xs ${t.muted} mt-0.5`}>
                        {la && <span>{fmtSym(la.symbol)} {la.direction.toUpperCase()} · </span>}
                        {lb && <span>{fmtSym(lb.symbol)} {lb.direction.toUpperCase()}</span>}
                        <span className="ml-2">
                          Ratio: {(pair.entry_ratio || 0).toFixed(4)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400">Kapalı</span>
                      <button onClick={() => deletePair(pair)}
                        className={`p-1 rounded ${t.muted} hover:text-rose-400 transition-colors`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
